import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import { ThemeProvider } from './context/ThemeContext';
import { PricingMarketProvider } from './context/PricingMarketContext';
import { AuthProvider } from './auth/AuthContext';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { OnboardingGate } from './auth/OnboardingGate';
import { ConfirmProvider } from './components/ui/ConfirmDialog';
import { PublicLayout } from './layouts/PublicLayout';
import { CustomerLayout } from './layouts/CustomerLayout';
import { AdminLayout } from './layouts/AdminLayout';
import { LoginPage } from './pages/public/LoginPage';
import { AdminLoginPage } from './pages/admin/AdminLoginPage';
import {
  ForgotPasswordPage,
  RegisterPage,
  ResetPasswordPage,
  VerifyEmailPage,
} from './pages/public/AuthPages';

const LandingPage = lazy(() =>
  import('./pages/public/LandingPage').then((m) => ({ default: m.LandingPage }))
);
const MarketplacePage = lazy(() =>
  import('./pages/public/MarketplacePage').then((m) => ({ default: m.MarketplacePage }))
);
const AppDetailPage = lazy(() =>
  import('./pages/public/AppDetailPage').then((m) => ({ default: m.AppDetailPage }))
);
const ApplicationsShowcasePage = lazy(() =>
  import('./pages/public/ApplicationsShowcasePage').then((m) => ({
    default: m.ApplicationsShowcasePage,
  }))
);
const ApplicationShowcasePage = lazy(() =>
  import('./pages/public/ApplicationShowcasePage').then((m) => ({
    default: m.ApplicationShowcasePage,
  }))
);
const DocumentationPage = lazy(() =>
  import('./pages/public/DocumentationPage').then((m) => ({ default: m.DocumentationPage }))
);
const PricingPage = lazy(() =>
  import('./pages/public/PricingPage').then((m) => ({ default: m.PricingPage }))
);
const KnowledgeBasePage = lazy(() =>
  import('./pages/public/KnowledgeBasePage').then((m) => ({ default: m.KnowledgeBasePage }))
);
const SystemStatusPage = lazy(() =>
  import('./pages/public/SystemStatusPage').then((m) => ({ default: m.SystemStatusPage }))
);
const AboutContactPage = lazy(() =>
  import('./pages/public/AboutContactPage').then((m) => ({ default: m.AboutContactPage }))
);
const ContactPage = lazy(() =>
  import('./pages/public/ContactPage').then((m) => ({ default: m.ContactPage }))
);

// Eager: post-Google OAuth lands here. Lazy + Suspense kept Login AuthChrome
// painted during load and stacked atmospheres/cards under welcome.
import { WelcomeExperience } from './pages/customer/WelcomeExperience';
const CustomerDashboard = lazy(() =>
  import('./pages/customer/CustomerDashboard').then((m) => ({ default: m.CustomerDashboard }))
);
const MyProductsPage = lazy(() =>
  import('./pages/customer/MyProductsPage').then((m) => ({ default: m.MyProductsPage }))
);
const LicenseKeysPage = lazy(() =>
  import('./pages/customer/LicenseKeysPage').then((m) => ({ default: m.LicenseKeysPage }))
);
const BoundDevicesPage = lazy(() =>
  import('./pages/customer/BoundDevicesPage').then((m) => ({ default: m.BoundDevicesPage }))
);
const DownloadsPage = lazy(() =>
  import('./pages/customer/DownloadsPage').then((m) => ({ default: m.DownloadsPage }))
);
const SubscriptionsPage = lazy(() =>
  import('./pages/customer/SubscriptionsPage').then((m) => ({ default: m.SubscriptionsPage }))
);
const InvoicesPage = lazy(() =>
  import('./pages/customer/InvoicesPage').then((m) => ({ default: m.InvoicesPage }))
);
const SupportTicketsPage = lazy(() =>
  import('./pages/customer/SupportTicketsPage').then((m) => ({ default: m.SupportTicketsPage }))
);
const CustomerProfilePage = lazy(() =>
  import('./pages/customer/CustomerProfilePage').then((m) => ({ default: m.CustomerProfilePage }))
);
const StartTrialPage = lazy(() =>
  import('./pages/hub/StartTrialPage').then((m) => ({ default: m.StartTrialPage }))
);
const UpgradePage = lazy(() =>
  import('./pages/hub/StartTrialPage').then((m) => ({ default: m.UpgradePage }))
);
const PaymentResultPage = lazy(() =>
  import('./pages/hub/PaymentResultPage').then((m) => ({ default: m.PaymentResultPage }))
);

const AdminDashboard = lazy(() =>
  import('./pages/admin/AdminDashboard').then((m) => ({ default: m.AdminDashboard }))
);
const AdminUserDirectory = lazy(() =>
  import('./pages/admin/AdminUserDirectory').then((m) => ({ default: m.AdminUserDirectory }))
);
const AdminCustomerDetailPage = lazy(() =>
  import('./pages/admin/AdminCustomerDetailPage').then((m) => ({
    default: m.AdminCustomerDetailPage,
  }))
);
const AdminPlatformSettingsPage = lazy(() =>
  import('./pages/admin/AdminPlatformSettingsPage').then((m) => ({
    default: m.AdminPlatformSettingsPage,
  }))
);
const AdminApplicationsPage = lazy(() =>
  import('./pages/admin/AdminApplicationsPage').then((m) => ({ default: m.AdminApplicationsPage }))
);
const AdminProductEditorPage = lazy(() =>
  import('./pages/admin/AdminProductEditorPage').then((m) => ({ default: m.AdminProductEditorPage }))
);
const AdminMediaManagerPage = lazy(() =>
  import('./pages/admin/AdminMediaManagerPage').then((m) => ({ default: m.AdminMediaManagerPage }))
);
const AdminReleasesPage = lazy(() =>
  import('./pages/admin/AdminReleasesPage').then((m) => ({ default: m.AdminReleasesPage }))
);
const AdminLicenseKillswitchPage = lazy(() =>
  import('./pages/admin/AdminLicenseKillswitchPage').then((m) => ({
    default: m.AdminLicenseKillswitchPage,
  }))
);
const AdminLicenseDetailPage = lazy(() =>
  import('./pages/admin/AdminLicenseDetailPage').then((m) => ({
    default: m.AdminLicenseDetailPage,
  }))
);
const AdminDevicesPage = lazy(() =>
  import('./pages/admin/AdminDevicesPage').then((m) => ({ default: m.AdminDevicesPage }))
);
const AdminDownloadsPage = lazy(() =>
  import('./pages/admin/AdminDownloadsPage').then((m) => ({ default: m.AdminDownloadsPage }))
);
const AdminAuditLogsPage = lazy(() =>
  import('./pages/admin/AdminAuditLogsPage').then((m) => ({ default: m.AdminAuditLogsPage }))
);
const AdminPlatformControlsPage = lazy(() =>
  import('./pages/admin/AdminPlatformControlsPage').then((m) => ({
    default: m.AdminPlatformControlsPage,
  }))
);
const AdminAccountPage = lazy(() =>
  import('./pages/admin/AdminAccountPage').then((m) => ({ default: m.AdminAccountPage }))
);
const AdminTrialsPage = lazy(() =>
  import('./pages/admin/AdminTrialsPage').then((m) => ({ default: m.AdminTrialsPage }))
);
const AdminSubscriptionsPage = lazy(() =>
  import('./pages/admin/AdminSubscriptionsPage').then((m) => ({ default: m.AdminSubscriptionsPage }))
);

function RouteFallback() {
  return (
    <div className="bc-route-fallback" role="status" aria-live="polite">
      <div className="bc-route-fallback__bar" aria-hidden>
        <span />
      </div>
      <span className="sr-only">Loading</span>
    </div>
  );
}

/**
 * Three independent experiences:
 * 1. Public Platform — browse without auth
 * 2. Customer Portal — Google Sign-In only
 * 3. Admin Platform — separate shell + password login → dashboard
 */
export function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <PricingMarketProvider>
        <AuthProvider>
          <ConfirmProvider>
              <BrowserRouter>
                <Suspense fallback={<RouteFallback />}>
                  <Routes>
                    {/* Customer Google sign-in */}
                    <Route path="/login" element={<LoginPage />} />
                    {/* Admin password sign-in — separate from customer */}
                    <Route path="/admin/login" element={<AdminLoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/verify-email" element={<VerifyEmailPage />} />

                    {/* 1. Public Platform (visitors) */}
                    <Route path="/" element={<PublicLayout />}>
                      <Route index element={<LandingPage />} />
                      <Route path="marketplace" element={<MarketplacePage />} />
                      <Route path="applications" element={<ApplicationsShowcasePage />} />
                      <Route path="applications/:slug" element={<ApplicationShowcasePage />} />
                      <Route path="products/:slug" element={<AppDetailPage />} />
                      <Route path="apps/:slug" element={<AppDetailPage />} />
                      <Route path="docs" element={<DocumentationPage />} />
                      <Route path="kb" element={<KnowledgeBasePage />} />
                      <Route path="status" element={<SystemStatusPage />} />
                      <Route path="about" element={<AboutContactPage />} />
                      <Route path="contact" element={<ContactPage />} />
                      <Route path="pricing" element={<PricingPage />} />
                      {/* Account-action pages are public to view; action requires Google session */}
                      <Route path="start-trial/:slug" element={<StartTrialPage />} />
                    </Route>

                    {/* 2. Customer Portal */}
                    <Route
                      path="/customer/welcome"
                      element={
                        <ProtectedRoute experience="customer" roles={['customer']}>
                          <WelcomeExperience />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/customer"
                      element={
                        <ProtectedRoute experience="customer" roles={['customer']}>
                          <OnboardingGate>
                            <CustomerLayout />
                          </OnboardingGate>
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<CustomerDashboard />} />
                      <Route path="products" element={<MyProductsPage />} />
                      <Route path="licenses" element={<LicenseKeysPage />} />
                      <Route path="devices" element={<BoundDevicesPage />} />
                      <Route path="downloads" element={<DownloadsPage />} />
                      <Route path="subscriptions" element={<SubscriptionsPage />} />
                      <Route path="upgrade" element={<UpgradePage />} />
                      <Route path="payment-result" element={<PaymentResultPage />} />
                      <Route path="invoices" element={<InvoicesPage />} />
                      <Route path="support" element={<SupportTicketsPage />} />
                      <Route path="profile" element={<CustomerProfilePage />} />
                    </Route>

                    {/* 3. Admin Platform — no public marketing chrome */}
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute experience="admin" roles={['platform_admin']}>
                          <AdminLayout />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<AdminDashboard />} />
                      <Route path="users" element={<AdminUserDirectory />} />
                      <Route path="users/:id" element={<AdminCustomerDetailPage />} />
                      <Route path="apps" element={<AdminApplicationsPage />} />
                      <Route path="apps/:id" element={<AdminProductEditorPage />} />
                      <Route path="media" element={<AdminMediaManagerPage />} />
                      <Route path="releases" element={<AdminReleasesPage />} />
                      <Route path="downloads" element={<AdminDownloadsPage />} />
                      <Route path="licenses" element={<AdminLicenseKillswitchPage />} />
                      <Route path="licenses/:id" element={<AdminLicenseDetailPage />} />
                      <Route path="devices" element={<AdminDevicesPage />} />
                      <Route path="trials" element={<AdminTrialsPage />} />
                      <Route path="subscriptions" element={<AdminSubscriptionsPage />} />
                      <Route path="auditlogs" element={<AdminAuditLogsPage />} />
                      <Route path="settings" element={<AdminPlatformControlsPage />} />
                      <Route path="settings/platform" element={<AdminPlatformSettingsPage />} />
                      <Route path="account" element={<AdminAccountPage />} />
                    </Route>

                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </BrowserRouter>
          </ConfirmProvider>
        </AuthProvider>
        </PricingMarketProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
